import * as Tabs from '@wandb/weave/components/Tabs';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import {JSONNaN} from '@wandb/weave/core';
import {clsx} from 'clsx';
import React, {useEffect, useMemo} from 'react';
import {Redirect, useHistory, useParams} from 'react-router-dom';

import {envIsLocal} from '../../config';
import {LoaderProxy} from '../../containers/AppLoader';
import {useRunDetailsQuery} from '../../generated/graphql';
import NoAccessWrapper from '../../pages/NoAccessPage/NoAccessWrapper';
import RunArtifacts from '../../pages/Run/RunArtifacts';
import RunOverview from '../../pages/Run/RunOverview';
import {setDocumentDescription, setDocumentTitle} from '../../util/document';
import {AccessOption} from '../../util/permissions';
import * as Run from '../../util/runs';
import * as urls from '../../util/urls';
import AnonymousClaimBanner from '../AnonymousClaimBanner';
import {CodeFiles} from '../CodeFiles';
import NoMatch from '../NoMatch';
import {SingleRunHeader} from '../PanelBank/headerControls/SingleRunHeader';
import RunFiles from '../RunFiles';
import RunModelInfo from '../RunModelInfo';
import {useRunDetailsTo} from './hooks';
import {RunPageLogs} from './RunPageLogs';

type RunParams = {
  entityName: string;
  projectName: string;
  runName: string;
  runTab?: string;
  filePath?: string;
};

type RunTabItem = {
  name: string;
  value: string;
  isShown: boolean;
  noSlug?: boolean;
  render: () => React.ReactNode;
};

type RunDetailsProps = {
  chartsContent: React.ReactNode;
};

export const RunDetails = ({chartsContent}: RunDetailsProps) => {
  const {entityName, projectName, runName, runTab, filePath} =
    useParams<RunParams>();
  const baseUrl = useRunDetailsTo(runName);

  const runDetailsQuery = useRunDetailsQuery({
    variables: {
      entityName,
      projectName,
      runName,
    },
  });

  const project = runDetailsQuery.data?.project;
  const run = project?.run;
  const isPublicOrOpenProject =
    project?.access === AccessOption.Public ||
    project?.access === AccessOption.Open;
  const inputArtifactsCount = run?.inputArtifacts?.totalCount ?? 0;
  const outputArtifactsCount = run?.outputArtifacts?.totalCount ?? 0;
  const hasArtifacts = inputArtifactsCount + outputArtifactsCount > 0;

  const history = useHistory();

  const tabDescription: string = useMemo(() => {
    switch (runTab) {
      case `overview`:
        return `Overview`;
      case 'charts':
        return 'Charts';
      case `tensorboard`:
        return `Tensorboard`;
      case `model`:
        return `Model`;
      case `models`:
        return `Models`;
      case `logs`:
        return `Logs`;
      case `files`:
        return 'Files';
      case `artifacts`:
        return 'Artifacts';
      case `code`:
        return 'Code';
      default:
        return 'Charts';
    }
  }, [runTab]);

  useEffect(() => {
    if (run == null) {
      return;
    }
    const runDisplayName = run.displayName ?? runName;
    setDocumentTitle(`${runDisplayName} | ${projectName}`);
    setDocumentDescription(
      `${tabDescription} of run ${runDisplayName} in ${projectName}, a machine learning project by ${entityName} using Weights & Biases.`
    );
  }, [tabDescription, run, runName, projectName, entityName]);

  if (runDetailsQuery.loading) {
    return <LoaderProxy name="run-query-loading-loader" />;
  } else if (project && project.entity.claimingEntity != null) {
    // If the run's entity has been claimed, redirect to the claiming entity
    // instead so the original link still works.
    return (
      <Redirect
        to={urls.run({
          entityName: project.entity.claimingEntity.name,
          projectName,
          name: runName,
        })}
      />
    );
  } else if (project == null || run == null) {
    return <NoMatch />;
  }
  const summaryMetrics = Run.parseSummary(run.summaryMetrics, run.name) || {};
  const config = JSONNaN.JSONparseNaN(run.config);
  // 'wandb_version' is an extra thing put in by the CLI that is a misnomer
  // and useless.
  delete config.wandb_version;

  const defaultTab = 'charts';
  const tabs: RunTabItem[] = [
    {
      name: 'Charts',
      value: 'charts',
      isShown: true,
      noSlug: true,
      render: () => chartsContent,
    },
    {
      name: 'Overview',
      value: 'overview',
      isShown: true,
      render: () => (
        <>
          <AnonymousClaimBanner
            entityName={entityName}
            projectName={projectName}
            run={runName}
            createdAt={run.createdAt}
          />
          <RunOverview
            entityName={entityName}
            projectName={projectName}
            runName={runName}
            summaryMetrics={summaryMetrics}
            config={config}
            history={history}
          />
        </>
      ),
    },
    {
      name: 'Tensorboard',
      value: 'tensorboard',
      isShown: Boolean(run.servicesAvailable?.tensorboard && !envIsLocal),
      render: () => (
        <div className="tensorboard-container">
          <iframe
            title="TensorBoard"
            className="tensorboard-iframe-consolidated"
            src={urls.tensorboardUrl(entityName, projectName, runName)}
          />
        </div>
      ),
    },
    {
      name: 'Model',
      value: 'model',
      isShown: Object.values(summaryMetrics).some(
        (val: any) =>
          val && (val._type === 'graph' || val._type === 'graph-file')
      ),
      render: () => (
        <>
          <AnonymousClaimBanner
            entityName={entityName}
            projectName={projectName}
            run={runName}
            createdAt={run.createdAt}
          />
          <RunModelInfo
            entityName={entityName}
            projectName={projectName}
            runName={runName}
            sweepName={run.sweep?.name}
            group={run.group ?? ''}
            summaryMetrics={summaryMetrics}
          />
        </>
      ),
    },
    {
      name: 'Logs',
      value: 'logs',
      isShown: true,
      render: () => (
        <RunPageLogs
          entityName={entityName}
          projectName={projectName}
          runName={runName}
        />
      ),
    },
    {
      name: 'Files',
      value: 'files',
      isShown: true,
      render: () => (
        <>
          <AnonymousClaimBanner
            entityName={entityName}
            projectName={projectName}
            run={runName}
            createdAt={run.createdAt}
          />
          <RunFiles
            entityName={entityName}
            projectName={projectName}
            runName={runName}
            path={filePath}
            history={history}
            groupName={run.group ?? ''}
            sweepName={run.sweep?.name}
          />
        </>
      ),
    },
    {
      name: 'Code',
      value: 'code',
      isShown:
        config._wandb?.value?.code_path ||
        config._wandb?.value?.session_history,
      render: () => (
        <CodeFiles
          runName={runName}
          entityName={entityName}
          projectName={projectName}
          filePath={filePath ?? ''}
        />
      ),
    },
    {
      name: `Artifacts`,
      value: 'artifacts',
      isShown: hasArtifacts,
      render: () => (
        <>
          <AnonymousClaimBanner
            entityName={entityName}
            projectName={projectName}
            run={runName}
            createdAt={run.createdAt}
          />
          <RunArtifacts
            entityName={entityName}
            projectName={projectName}
            runName={runName}
            groupName={run.group ?? ''}
            sweepName={run.sweep?.name}
          />
        </>
      ),
    },
  ].filter(tab => tab.isShown);

  return (
    <TailwindContents>
      <div className={clsx('w-full h-full flex flex-col')}>
        <NoAccessWrapper skipCheck={isPublicOrOpenProject}>
          <div className="font-semibold">
            <SingleRunHeader />
          </div>
          <Tabs.Root
            value={runTab ?? defaultTab}
            className="flex flex-col min-h-0 flex-1">
            <div className="pt-16 px-16">
              <Tabs.List>
                {tabs.map(tab => (
                  <Tabs.LinkedTrigger
                    key={tab.value}
                    value={tab.value}
                    to={tab.noSlug ? baseUrl() : baseUrl(tab.value)}>
                    {tab.name}
                  </Tabs.LinkedTrigger>
                ))}
              </Tabs.List>
            </div>
            <div className={clsx('flex-1 min-h-0 overflow-auto bg-white')}>
              {tabs.map(tab => (
                <Tabs.Content
                  className="h-full"
                  key={tab.value}
                  value={tab.value}>
                  {tab.render()}
                </Tabs.Content>
              ))}
            </div>
          </Tabs.Root>
        </NoAccessWrapper>
      </div>
    </TailwindContents>
  );
};
